import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useFormatDateTime } from '../util/displayTimeZone';
import * as instancesApi from '../api/procedureInstances';
import { listCaptureSessions, type CaptureSession } from '../api/sessions';
import { listMyCaseCandidates } from '../api/procedureInstances';
import { RecordingFilters } from './RecordingFilters';
import {
  mergeOptions,
  useDebounced,
  useSessionFilterOptions,
} from './serverFilter';
import {
  EMPTY_FILTER,
  filterOptions,
  matchesFilter,
  type RecordingFilter,
} from './recordingFilterRules';
import {
  suggestConcurrentCases,
  windowsOverlap,
  type RecordingWindow,
} from '../util/caseSuggestions';

/**
 * Build a "case" (MULTI-PARTICIPANT-FOCUS): tag several recordings into one
 * clinical event so the Focus overlay can compare the team across it.
 *
 * No backend change — creates a procedure instance (name → external_case_ref)
 * and attaches the chosen recordings. The name auto-labels as
 *   Procedure - Participant - Participant - DateTime
 * from the selection, and stays editable. Procedure + participant filters
 * narrow the list (a case is one procedure).
 */

/** The recording's display name — matches the Dashboard/Activity, which use
 *  the operator's `label` and fall back to `procedure_name` (CaptureTimeline).
 *  `procedure_name` alone is often a placeholder like "Test". */
function recName(r: CaptureSession): string {
  return (r.label || r.procedure_name || 'Session').trim();
}

function autoLabel(rows: CaptureSession[], fmt: (v: string) => string): string {
  if (rows.length === 0) return '';
  const proc = rows.map(recName).find(Boolean) ?? 'Case';
  const names = [...new Set(rows.map((r) => (r.participant_label || 'Unknown').trim()))];
  const start = rows.map((r) => r.started_at).sort()[0];
  return [proc, ...names, start ? fmt(start) : ''].filter(Boolean).join(' - ');
}

/** Map a capture-session row onto the time-window shape the case-suggestion
 *  helpers work with (participant keyed by its display label). */
function toWindow(r: CaptureSession): RecordingWindow {
  return {
    id: r.id,
    participant: r.participant_label || 'Unknown',
    startedAt: r.started_at,
    endedAt: r.ended_at,
  };
}

interface Props {
  tenantId: string;
  open: boolean;
  /** When set, edit this case's assembly (pre-selects its recordings + name)
   *  instead of creating a new one. */
  editInstanceId?: string | null;
  onClose: () => void;
  onSaved: (instanceId: string) => void;
  /**
   * Build the case as a participant rather than as staff.
   *
   * Changes both ends: the picker is fed by `/me/cases/candidates` (their own
   * recordings plus anyone co-present) instead of the tenant roster they have
   * no right to, and saving goes to `/me/cases`, which derives the tenant from
   * the selection. Editing stays staff-only — a participant who could re-open
   * a case could detach the very recording that grants them access to it.
   */
  asParticipant?: boolean;
}


export function CreateCaseDialog({
  tenantId,
  open,
  editInstanceId,
  onClose,
  onSaved,
  asParticipant = false,
}: Props) {
  const fmt = useFormatDateTime();
  const editing = editInstanceId != null;
  const [sessions, setSessions] = useState<CaptureSession[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [originalIds, setOriginalIds] = useState<string[]>([]);
  const [originalName, setOriginalName] = useState('');
  const [procFilter, setProcFilter] = useState<string | null>(null);
  // Participants / device / date / text come from the shared filter, so this
  // dialog asks the same questions as Activity and the dashboards. Procedure
  // stays its own single-select below: here it is not merely a filter, it also
  // drives auto-suggest, which needs exactly one procedure to cluster within.
  const [filter, setFilter] = useState<RecordingFilter>(EMPTY_FILTER);
  const [name, setName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Auto-suggest: how much wall-clock gap still counts as "same case".
  const [marginMin, setMarginMin] = useState(10);
  // Options span the tenant's whole history, not the fetched page — the
  // recordings you are building a case from are often the older ones.
  // Staff get the tenant's whole history; a participant's candidate list is
  // already small and bounded by co-presence, so deriving options from it is
  // both sufficient and all they are entitled to.
  const filterSource = useSessionFilterOptions(
    open && !asParticipant ? tenantId : null,
  );
  // Only the date window goes to the server here.
  //
  // Not the participant: auto-suggest exists to find several people recording
  // the SAME case concurrently, so narrowing the fetch by participant would
  // hide exactly the rows it needs to cluster. And not the procedure: picking
  // one is how you START clustering, and a refetch on every pick would throw
  // away the selection mid-build. Those two stay client-side passes over the
  // fetched set, where they cost nothing.
  const dateWindow = useDebounced({ from: filter.from, to: filter.to });

  // Two effects, not one. Opening the dialog resets the form; changing the
  // date window re-queries. Folding the re-query into the reset would make a
  // date selection clear the filter that caused it — a loop that wipes the
  // user's input the moment they type a date.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setOriginalIds([]);
    setOriginalName('');
    setProcFilter(null);
    setFilter({ ...EMPTY_FILTER });
    setName('');
    setNameDirty(editing); // keep the saved name until the user edits it
    setError(null);
    if (editInstanceId) {
      instancesApi
        .getInstance(tenantId, editInstanceId)
        .then((detail) => {
          const ids = detail.sessions.map((s) => s.id);
          setSelected(new Set(ids));
          setOriginalIds(ids);
          const nm = detail.external_case_ref ?? '';
          setName(nm);
          setOriginalName(nm);
        })
        .catch(() => setError('Could not load the case.'));
    }
  }, [open, tenantId, editInstanceId, editing]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSessions(null);
    (asParticipant
      ? // Co-presence is computed server-side against this participant's own
        // recordings; there is no tenant roster to narrow, so the date
        // window is applied client-side below like any other filter.
        listMyCaseCandidates()
      : listCaptureSessions(tenantId, false, {
          startedFrom: dateWindow.from || undefined,
          startedTo: dateWindow.to || undefined,
        }))
      .then((rows) => {
        if (cancelled) return;
        setSessions(
          rows
            // Started recordings only, and never disabled (soft-deleted) ones —
            // the list endpoint already hides them, this is belt-and-suspenders.
            .filter((r) => r.started_at && !r.deleted_at)
            .sort((a, b) => b.started_at.localeCompare(a.started_at)),
        );
      })
      .catch(() => {
        if (!cancelled) setError('Could not load recordings.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId, asParticipant, JSON.stringify(dateWindow)]);

  const selectedRows = useMemo(
    () => (sessions ?? []).filter((r) => selected.has(r.id)),
    [sessions, selected],
  );

  // Keep the name auto-labelled until the user edits it.
  useEffect(() => {
    if (!nameDirty) setName(autoLabel(selectedRows, fmt));
  }, [selectedRows, nameDirty, fmt]);

  const filterable = useMemo(
    () =>
      (sessions ?? []).map((r) => ({
        participantLabel: r.participant_label || 'Unknown',
        deviceLabel: r.device_label ?? null,
        procedureName: recName(r),
        startedAt: r.started_at,
      })),
    [sessions],
  );
  const filterOpts = useMemo(
    () => mergeOptions(filterSource.options, filterOptions(filterable)),
    [filterSource.options, filterable],
  );

  const filtered = useMemo(() => {
    return (sessions ?? []).filter(
      (r) =>
        (!procFilter || recName(r) === procFilter) &&
        matchesFilter(
          {
            participantLabel: r.participant_label || 'Unknown',
            deviceLabel: r.device_label ?? null,
            procedureName: recName(r),
            startedAt: r.started_at,
          },
          filter,
        ),
    );
  }, [sessions, procFilter, filter]);

  // Auto-suggested cases for the chosen procedure: cluster its not-yet-assigned
  // recordings on wall-clock proximity (±marginMin) into multi-participant
  // groups. Only meaningful once a procedure is picked (a case is one
  // procedure); recompute live as the margin slider moves.
  const suggestions = useMemo(() => {
    if (!procFilter || !sessions) return [];
    const rows = sessions.filter(
      (r) =>
        recName(r) === procFilter &&
        !r.procedure_instance_id &&
        // A recording with no uploaded NDJSON can never yield a focus trace,
        // so it can't contribute to a case — don't suggest it.
        r.s3_key,
    );
    return suggestConcurrentCases(rows.map(toWindow), marginMin);
  }, [procFilter, sessions, marginMin]);

  const applySuggestion = (recordingIds: string[]) => {
    setSelected(new Set(recordingIds));
    setNameDirty(false); // let the name auto-label from the picked case
  };

  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function save() {
    if (selected.size === 0 || !name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      if (editInstanceId) {
        if (name.trim() !== originalName) {
          await instancesApi.updateInstance(tenantId, editInstanceId, {
            external_case_ref: name.trim(),
          });
        }
        const orig = new Set(originalIds);
        const added = [...selected].filter((id) => !orig.has(id));
        const removed = originalIds.filter((id) => !selected.has(id));
        await Promise.all(added.map((id) => instancesApi.attachSession(tenantId, editInstanceId, id)));
        await Promise.all(removed.map((id) => instancesApi.detachSession(tenantId, editInstanceId, id)));
        onSaved(editInstanceId);
      } else if (asParticipant) {
        // One call, not create-then-attach: a participant cannot repair a
        // case that was created and then failed to take its recordings.
        const inst = await instancesApi.createMyCase({
          procedure_name: selectedRows.map(recName).find(Boolean) ?? 'Case',
          external_case_ref: name.trim(),
          session_ids: selectedRows.map((r) => r.id),
        });
        onSaved(inst.id);
      } else {
        const proc = selectedRows.map(recName).find(Boolean) ?? 'Case';
        const inst = await instancesApi.createInstance(tenantId, {
          procedure_name: proc,
          external_case_ref: name.trim(),
          status: 'completed',
        });
        await Promise.all(
          selectedRows.map((r) => instancesApi.attachSession(tenantId, inst.id, r.id)),
        );
        onSaved(inst.id);
      }
    } catch {
      setError(editing ? 'Could not save the case.' : 'Could not create the case.');
    } finally {
      setCreating(false);
    }
  }

  const overlap =
    selectedRows.length >= 2 ? windowsOverlap(selectedRows.map(toWindow)) : null;

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit case' : 'New case'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Filter to a procedure and the people you want, then tick the
            recordings that belong to one clinical event. Two or more people
            whose times overlap gives the richest comparison.
          </Typography>

          {/* Filters — shared with Activity and the dashboards, so the same
              question is asked the same way wherever you are. */}
          <RecordingFilters
            value={filter}
            onChange={setFilter}
            options={filterOpts}
            // Procedure has its own single-select below (auto-suggest needs
            // exactly one). Device is irrelevant here: a case is defined by
            // who operated and when, not by which tablet recorded it.
            show={{ procedures: false, devices: false }}
            // The denominator is the fetched set, which a date window has
            // already narrowed — so say what it counts rather than implying
            // it is the tenant's whole history.
            summary={`${filtered.length} of ${(sessions ?? []).length} loaded`}
          >
            <Autocomplete
              sx={{ minWidth: 220, flexGrow: 1 }}
              size="small"
              options={filterOpts.procedures}
              value={procFilter}
              onChange={(_, v) => setProcFilter(v)}
              renderInput={(params) => (
                <TextField {...params} label="Procedure" />
              )}
            />
          </RecordingFilters>

          {procFilter && sessions && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'action.hover',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
                  Suggested cases
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  match ±{marginMin} min
                </Typography>
                <Slider
                  size="small"
                  value={marginMin}
                  onChange={(_, v) => setMarginMin(v as number)}
                  min={0}
                  max={30}
                  step={5}
                  marks
                  valueLabelDisplay="auto"
                  sx={{ width: 130, flexShrink: 0 }}
                  aria-label="Overlap match margin in minutes"
                />
              </Stack>
              {suggestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No overlapping multi-participant groups for “{procFilter}” at
                  ±{marginMin} min. Widen the match, or tick recordings manually
                  below.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {suggestions.map((c, i) => {
                    const spanMin = Math.round(
                      (Date.parse(c.endedAt) - Date.parse(c.startedAt)) / 60000,
                    );
                    const active =
                      selected.size === c.recordingIds.length &&
                      c.recordingIds.every((id) => selected.has(id));
                    return (
                      <Box
                        key={c.recordingIds.join(',')}
                        sx={{
                          border: 1,
                          borderColor: active ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
                            Case {i + 1}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {fmt(c.startedAt)} · {spanMin} min ·{' '}
                            {c.participants.length} people ·{' '}
                            {c.recordingIds.length} recordings
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Button
                            size="small"
                            variant={active ? 'contained' : 'outlined'}
                            onClick={() => applySuggestion(c.recordingIds)}
                          >
                            {active ? 'Selected' : 'Select'}
                          </Button>
                        </Stack>
                        <Stack
                          direction="row"
                          sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {c.participants.map((p) => (
                            <Chip key={p} size="small" label={p} />
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}

          <Box sx={{ maxHeight: 240, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {sessions === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No recordings match these filters.
              </Typography>
            ) : (
              <List dense disablePadding>
                {filtered.map((r) => {
                  const mins = r.ended_at
                    ? Math.round((Date.parse(r.ended_at) - Date.parse(r.started_at)) / 60000)
                    : null;
                  return (
                    <ListItemButton key={r.id} onClick={() => toggle(r.id)} dense>
                      <Checkbox edge="start" checked={selected.has(r.id)} tabIndex={-1} disableRipple />
                      <ListItemText
                        primary={`${r.participant_label || 'Unknown'} · ${recName(r)}`}
                        secondary={`${fmt(r.started_at)}${mins != null ? ` · ${mins} min` : ''}${
                          r.s3_key ? '' : ' · no recording uploaded'
                        }`}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{selectedRows.length} selected</Typography>
            {overlap === true && <Chip size="small" color="success" label="times overlap" />}
            {overlap === false && (
              <Chip size="small" color="default" variant="outlined" label="no time overlap" />
            )}
          </Stack>

          <TextField
            label="Case name"
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameDirty(true);
            }}
            helperText="Procedure - participant - participant - date · edit as you like."
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={creating || selected.size === 0 || !name.trim()}
        >
          {creating ? 'Saving…' : editing ? 'Save changes' : 'Create case'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
